import React, {useCallback, useContext, useMemo, useState} from 'react';

import {EMAIL_SETTINGS} from 'src/constants';
import {MessageContext} from 'src/contexts/message-context';
import {useAccountManagerTypes} from 'src/hooks/dictionaries';
import {EmailType as EmailTypes} from 'src/openapi/admin/models/EmailType';

import Button from '@pb/kit-component-button';
import {CheckBox} from '@pb/kit-component-checkbox';
import {Dialog, DialogFooter, DialogHeader, DialogMain} from '@pb/kit-component-dialog-v2';
import {Group} from '@pb/kit-component-group';

import {useSendAmNoGiftSsoWelcomeEmail} from './use-cases/useSendAmNoGiftSsoWelcomeEmail';
import {useSendAmNoGiftWelcomeEmail} from './use-cases/useSendAmNoGiftWelcomeEmail';
import {useSendAmSsoWelcomeEmail} from './use-cases/useSendAmSsoWelcomeEmail';
import {useSendAmTrialWelcomeEmail} from './use-cases/useSendAmTrialWelcomeEmail';
import {useSendAmWelcomeEmail} from './use-cases/useSendAmWelcomeEmail';
import {useSendLlmOnlyWelcomeEmail} from './use-cases/useSendLlmOnlyWelcomeEmail';
import {useSendNewClientWelcomeEmail} from './use-cases/useSendNewClientWelcomeEmail';
import {useSendOriginWelcomeEmail} from './use-cases/useSendOriginWelcomeEmail';
import {useSendSmbWelcomeEmail} from './use-cases/useSendSmbWelcomeEmail';
import {useSendStartupWelcomeEmail} from './use-cases/useSendStartupWelcomeEmail';
import {useSendWelcomeEmail} from './use-cases/useSendWelcomeEmail';

import type {AccountManagerInfo} from 'src/openapi/admin/models/AccountManagerInfo';
import type {EmailType} from 'src/openapi/admin/models/EmailType';
import type {CheckBoxChangeData} from 'src/typedef';


type ConfirmDialogProps = {
    emailType: EmailType,
    teamMembers: AccountManagerInfo[],
    showConfirmDialog: boolean,
    userId: number,
    closeConfirmDialog: () => void,
};

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
    emailType,
    showConfirmDialog,
    teamMembers,
    userId,
    closeConfirmDialog,
}) => {
    const [selectedTeamMembers, setSelectedTeamMembers] = useState<AccountManagerInfo[]>([]);
    const {setSuccessMessage, setErrorMessage} = useContext(MessageContext);
    const [mailSendProgress, setMailSendProgress] = useState(false);
    const {getAccountManagerTypeByCode} = useAccountManagerTypes();

    const {event: sendWelcomeEmail} = useSendWelcomeEmail({userId});
    const {event: sendAmWelcomeEmail} = useSendAmWelcomeEmail({userId});
    const {event: sendSmbWelcomeEmail} = useSendSmbWelcomeEmail({userId});
    const {event: sendAmSsoWelcomeEmail} = useSendAmSsoWelcomeEmail({userId});
    const {event: sendStartupWelcomeEmail} = useSendStartupWelcomeEmail({userId});
    const {event: sendNewClientWelcomeEmail} = useSendNewClientWelcomeEmail({userId});
    const {event: sendAmTrialWelcomeEmail} = useSendAmTrialWelcomeEmail({userId});
    const {event: sendAmNoGiftWelcomeEmail} = useSendAmNoGiftWelcomeEmail({userId});
    const {event: sendAmNoGiftSsoWelcomeEmail} = useSendAmNoGiftSsoWelcomeEmail({userId});
    const {event: sendOriginWelcomeEmail} = useSendOriginWelcomeEmail({userId});
    const {event: sendLlmOnlyWelcomeEmail} = useSendLlmOnlyWelcomeEmail({userId});

    const isTeamConfigured = useMemo(() => {
        const result = teamMembers.length <= 2 &&
            teamMembers.find(({managerType}) => managerType === 'AM') &&
            teamMembers.filter(({managerType}) => managerType)
                .map(({managerType}) => managerType ?? '')
                .every(managerType => ['PRIMARY_CSM', 'AM'].includes(managerType));

        if (result) {
            setSelectedTeamMembers(teamMembers);
        }

        return result;
    }, [teamMembers]);

    const {amCsmRequired, supportFallback, confirmMessage, noTeamConfirmMessage, infoPopover}
        = EMAIL_SETTINGS[emailType];
    const hasAccountTeam = useMemo(() => teamMembers.some(({managerType}) => managerType), [teamMembers]);
    // Emails with a Support fallback keep the selection optional, but the admin may still pick contacts
    const showTeamSelection = hasAccountTeam && !isTeamConfigured && (amCsmRequired || supportFallback);
    const message = !hasAccountTeam && noTeamConfirmMessage
        ? noTeamConfirmMessage
        : confirmMessage ?? infoPopover;

    const changeEmailAccountTeam = useCallback((changeData: CheckBoxChangeData) => {
        const {event, name} = changeData;
        const changedMember = teamMembers.find(({id}) => id.toString() === name);

        setSelectedTeamMembers(prevState => [
            ...prevState.filter(({id}) => id.toString() !== name),
            ...event.currentTarget.checked && changedMember ? [changedMember] : [],
        ]);
    }, [teamMembers]);

    const sendEmail = useCallback(async () => {
        const selectTeamMemberIds = selectedTeamMembers
            .filter(({managerType}) => managerType)
            .map(({id}) => id);

        switch (emailType) {
            case EmailTypes.AM_WELCOME_EMAIL:
                return await sendAmWelcomeEmail(selectTeamMemberIds);
            case EmailTypes.SMB_WELCOME_EMAIL:
                return await sendSmbWelcomeEmail();
            case EmailTypes.STARTUP_WELCOME_EMAIL:
                return await sendStartupWelcomeEmail();
            case EmailTypes.NEW_CLIENT_WELCOME_EMAIL:
                return await sendNewClientWelcomeEmail();
            case EmailTypes.WELCOME_EMAIL:
                return await sendWelcomeEmail();
            case EmailTypes.AM_WELCOME_EMAIL_SSO:
                return await sendAmSsoWelcomeEmail(selectTeamMemberIds);
            case EmailTypes.AM_WELCOME_EMAIL_TRIAL:
                return await sendAmTrialWelcomeEmail(selectTeamMemberIds);
            case EmailTypes.AM_WELCOME_EMAIL_NO_GIFT:
                return await sendAmNoGiftWelcomeEmail(selectTeamMemberIds);
            case EmailTypes.AM_WELCOME_EMAIL_SSO_NO_GIFT:
                return await sendAmNoGiftSsoWelcomeEmail(selectTeamMemberIds);
            case EmailTypes.ORIGIN_WELCOME_EMAIL:
                return await sendOriginWelcomeEmail();
            case EmailTypes.LLM_ONLY_WELCOME_EMAIL:
                return await sendLlmOnlyWelcomeEmail(selectTeamMemberIds);
            default:
                return new Error('Unknown email type').message;
        }
    }, [
        emailType,
        selectedTeamMembers,
        sendAmWelcomeEmail,
        sendSmbWelcomeEmail,
        sendStartupWelcomeEmail,
        sendWelcomeEmail,
        sendAmSsoWelcomeEmail,
        sendAmTrialWelcomeEmail,
        sendAmNoGiftWelcomeEmail,
        sendAmNoGiftSsoWelcomeEmail,
        sendNewClientWelcomeEmail,
        sendOriginWelcomeEmail,
        sendLlmOnlyWelcomeEmail,
    ]);

    const onClose = useCallback(() => {
        closeConfirmDialog();
        if (!isTeamConfigured) {
            setSelectedTeamMembers([]);
        }
    }, [closeConfirmDialog, isTeamConfigured, setSelectedTeamMembers]);

    const sendWelcomeEmailHandler = useCallback(async () => {
        setMailSendProgress(true);
        const error = await sendEmail();

        if (error) {
            setErrorMessage(error);
        } else {
            setSuccessMessage('Welcome email is successfully sent');
        }
        setMailSendProgress(false);
        onClose();
    }, [
        onClose,
        sendEmail,
        setErrorMessage,
        setSuccessMessage,
        setMailSendProgress,
    ]);

    if (!emailType) {
        return null;
    }

    return (
        <Dialog
            shown={showConfirmDialog}
            setShown={onClose}>
            <DialogHeader onClose={onClose}>
                {EMAIL_SETTINGS[emailType].label}
            </DialogHeader>
            <DialogMain style={{padding: 24}}>
                <Group direction="v">
                    <p>{message}</p>
                    <p>Do you want to send the email?</p>
                    {showTeamSelection ? (
                        <Group direction="v">
                            <p>Please select the account managers that will be mentioned in the email:</p>
                            {teamMembers.filter(({managerType}) => managerType).map(teamMember => (
                                <CheckBox
                                    checked={selectedTeamMembers.map(({id}) => id).includes(teamMember.id)}
                                    name={teamMember.id.toString()}
                                    key={teamMember.id}
                                    label={`${teamMember.fullName}
                            ${getAccountManagerTypeByCode(teamMember?.managerType ?? '')?.description}`}
                                    onCheckBoxChange={changeEmailAccountTeam}
                                />
                            ))}
                        </Group>
                    ) : null}
                </Group>
            </DialogMain>
            <DialogFooter>
                <Group>
                    <Button
                        variant="Type.PRIMARY"
                        busy={mailSendProgress}
                        disabled={amCsmRequired ? !selectedTeamMembers.length : undefined}
                        onClick={sendWelcomeEmailHandler}>
                        Send
                    </Button>
                    <Button variant="Type.SECONDARY" onClick={onClose}>
                        Cancel
                    </Button>
                </Group>
            </DialogFooter>
        </Dialog>
    );
};
